import { createAction, createReducer, createSelector } from "@reduxjs/toolkit"
import { isEmptyFields, toSearchParams } from "helpers/form"
import { formatPaginationParams } from "helpers/pagination"
import { find, isEqual, reject } from "lodash"
import { createdFavouriteTrips, deletedFavouriteTrips } from "store/favourite-trips"
import { api } from "./helpers/api"
import { collectionSelector, modelSelector } from "./selectors"
import { LOADING_INITIAL_STATE, setFailed, setReceived, setRequested } from "modules/loader-watchers/helpers/loading"
import { PAGINATION_INITIAL_STATE } from "./constants"

const EMPTY_DRAFT_FIELDS = [
  "title",
  "description",
  "start_date",
  "end_date",
  "max_guests",
  "min_guests",
  "photos",
  "skill_level",
  "bring_items",
  "included_items",
  "target_fish_species",
  "fishing_activity_type",
  "fishing_ecosystem"
]

// ACTIONS
export const setCalendarDates = createAction("trips/setCalendarDates")
const requested = createAction("trips/requested")
const received = createAction("trips/received")
const failed = createAction("trips/failed")
const cleaned = createAction("trips/cleaned")
const requestedTrip = createAction("trips/requestedTrip")
export const receivedTrip = createAction("trips/receivedTrip")
const failedTrip = createAction("trips/failedTrip")
const createdTrip = createAction("trips/createdTrip")
const requestedUpdateTrip = createAction("trips/requestedUpdateTrip")
const updatedTrip = createAction("trips/updatedTrip")
const deletedTrip = createAction("trips/deletedTrip")
const cleanedTrip = createAction("trips/cleanedTrip")
// Bulk actions operate on many trips at once; the caller refetches the list on
// success, so these just toggle the trip loading flag and never write the bulk
// response into state.trip (which would otherwise pollute the single-trip slice).
const requestedBulkTrips = createAction("trips/requestedBulkTrips")
const succeededBulkTrips = createAction("trips/succeededBulkTrips")
const failedBulkTrips = createAction("trips/failedBulkTrips")
const requestedFishSpecies = createAction("trips/requestedFishSpecies")
const receivedFishSpecies = createAction("trips/receivedFishSpecies")
const failedFishSpecies = createAction("trips/failedFishSpecies")
const requestedSuggestCategory = createAction("trips/requestedSuggestCategory")
const receivedSuggestCategory = createAction("trips/receivedSuggestCategory")
const failedSuggestCategory = createAction("trips/failedSuggestCategory")
const requestedSuggestMarketplaceTag = createAction("trips/requestedSuggestMarketplaceTag")
const receivedSuggestMarketplaceTag = createAction("trips/receivedSuggestMarketplaceTag")
const failedSuggestMarketplaceTag = createAction("trips/failedSuggestMarketplaceTag")
const requestedFilterOptions = createAction("trips/requestedFilterOptions")
const receivedFilterOptions = createAction("trips/receivedFilterOptions")
const failedFilterOptions = createAction("trips/failedFilterOptions")
const updatedTripColors = createAction("trips/updatedTripColors")
export const updatedAvailableDays = createAction("trips/updatedAvailableDays")
export const updatedAvailableHours = createAction("trips/updatedAvailableHours")
export const updatedSeasonal = createAction("trips/updatedSeasonal")
const searchRequested = createAction("trips/searchRequested")
const searchReceived = createAction("trips/searchReceived")
const searchFailed = createAction("trips/searchFailed")

const getCategoriesRequest = createAction("trips/getCategoriesRequest")
const getCategoriesReceived = createAction("trips/getCategoriesReceived")
const getCategoriesFailed = createAction("trips/getCategoriesFailed")
const createCategoryRequest = createAction("trips/createCategoryRequest")
const createCategoryReceived = createAction("trips/createCategoryReceived")
const createCategoryFailed = createAction("trips/createCategoryFailed")
const updateCategoryRequest = createAction("trips/updateCategoryRequest")
const updateCategoryReceived = createAction("trips/updateCategoryReceived")
const updateCategoryFailed = createAction("trips/updateCategoryFailed")
const deleteCategoryRequest = createAction("trips/deleteCategoryRequest")
const deleteCategoryReceived = createAction("trips/deleteCategoryReceived")
const deleteCategoryFailed = createAction("trips/deleteCategoryFailed")

const getMarketplaceTagsRequest = createAction("trips/getMarketplaceTagsRequest")
const getMarketplaceTagsReceived = createAction("trips/getMarketplaceTagsReceived")
const getMarketplaceTagsFailed = createAction("trips/getMarketplaceTagsFailed")

const getAddOnCategoriesRequest = createAction("trips/getAddOnCategoriesRequest")
const getAddOnCategoriesReceived = createAction("trips/getAddOnCategoriesReceived")
const getAddOnCategoriesFailed = createAction("trips/getAddOnCategoriesFailed")
const createAddOnCategoryRequest = createAction("trips/createAddOnCategoryRequest")
const createAddOnCategoryReceived = createAction("trips/createAddOnCategoryReceived")
const createAddOnCategoryFailed = createAction("trips/createAddOnCategoryFailed")

const getExperienceOptionsRequest = createAction("trips/getExperienceOptionsRequest")
const getExperienceOptionsReceived = createAction("trips/getExperienceOptionsReceived")
const getExperienceOptionsFailed = createAction("trips/getExperienceOptionsFailed")
const addExperienceOptionRequest = createAction("trips/addExperienceOptionRequest")
const addExperienceOptionReceived = createAction("trips/addExperienceOptionReceived")
const addExperienceOptionFailed = createAction("trips/addExperienceOptionFailed")

const getTripCategoriesRequest = createAction("trips/getTripCategoriesRequest")
const getTripCategoriesReceived = createAction("trips/getTripCategoriesReceived")
const getTripCategoriesFailed = createAction("trips/getTripCategoriesFailed")
const getTripSubcategoriesRequest = createAction("trips/getTripSubcategoriesRequest")
const getTripSubcategoriesReceived = createAction("trips/getTripSubcategoriesReceived")
const getTripSubcategoriesFailed = createAction("trips/getTripSubcategoriesFailed")
export const seedTripSubcategories = createAction("trips/seedTripSubcategories")
const getPublicSubcategoriesRequest = createAction("trips/getPublicSubcategoriesRequest")
const getPublicSubcategoriesReceived = createAction("trips/getPublicSubcategoriesReceived")
const getPublicSubcategoriesFailed = createAction("trips/getPublicSubcategoriesFailed")

export const CLEAR_ALL_FILTERS = "CLEAR_ALL_FILTERS"

export const clearAllFilters = () => ({ type: CLEAR_ALL_FILTERS })

export const setPriceRange = createAction("trips/setPriceRange")
export const setLanguage = createAction("trips/setLanguage")
export const setSkillLevel = createAction("trips/setSkillLevel")
export const setExperiences = createAction("trips/setExperiences")
export const setSubcategories = createAction("trips/setSubcategories")
export const setOther = createAction("trips/setOther")
export const setGuests = createAction("trips/setGuests")
export const setDates = createAction("trips/setDates")
export const setSort = (payload) => ({ type: "SET_SORT", payload })
export const setGuestCount = createAction("trips/setGuestCount")
export const setView = createAction("trips/setView")

// REDUCER
const initialState = {
  ...LOADING_INITIAL_STATE,
  items: [],
  searchItems: [],
  searchLoading: false,
  searchError: null,
  fish_species: {
    ...LOADING_INITIAL_STATE,
    items: []
  },
  suggested_categories: {
    ...LOADING_INITIAL_STATE,
    items: []
  },
  filter_options: {
    ...LOADING_INITIAL_STATE,
    lists: {}
  },
  metadata: {
    ...PAGINATION_INITIAL_STATE
  },
  trip: {
    ...LOADING_INITIAL_STATE,
    id: null,
    title: null,
    description: null,
    status: "",
    fishing_activity_type: null,
    trip_length: "",
    min_guests: null,
    max_guests: null,
    seasonal: false,
    start_date: null,
    end_date: null,
    recurring_yearly: false,
    available_date_ranges: [],
    pricing_type: null,
    price_per_person: 0,
    flat_rate: 0,
    minimum_rate: 0,
    minimum_rate_person_count: 0,
    rate_per_additional_person: 0,
    latitude: null,
    longitude: null,
    available_days: {
      friday: false,
      monday: false,
      saturday: false,
      sunday: false,
      thursday: false,
      tuesday: false,
      wednesday: false
    },
    bring_items: [],
    included_items: [],
    target_fish_species: [],
    photos: [],
    media: [],
    skill_level: null,
    alcohol_allowed: false,
    accessibility_enabled: false,
    fishing_ecosystem: null,
    wizard_completed: null,
    wizard_step: "",
    existing_draft_booking: null,
    boat_ids: []
  },
  categories: {
    loading: false,
    error: null,
    data: {}
  },
  marketplaceTags: {
    loading: false,
    error: null,
    data: []
  },
  addOnCategories: {
    loading: false,
    error: null,
    data: []
  },
  experienceOptions: {
    loading: false,
    error: null,
    data: null
  },
  tripCategories: {
    loading: false,
    error: null,
    data: []
  },
  tripSubcategories: {
    loading: false,
    error: null,
    data: []
  },
  // Per-category subcategory cache for the public guest filter (keyed by category id)
  publicSubcategories: {
    byCategory: {}
  },
  priceRange: [],
  language: [],
  skillLevel: [],
  experiences: [],
  subcategories: [],
  other: [],
  guests: { adults: 0, children: 0 },
  calendar: { from_date: "", to_date: "" },
  sort: { price_sort: "", distance_sort: "asc" },
  view: "map" // trips page layout: "list" | "map"
}

// HELPERS
const isEmptyDraft = (item) => isEmptyFields(item, EMPTY_DRAFT_FIELDS) && isEqual(item.available_days, initialState.trip.available_days)

const tripsReducer = createReducer(initialState, {
  [requested.type]: (state) => {
    setRequested(state)
  },
  [received.type]: (state, action) => {
    setReceived(state)
    state.items = action.props.reset ? action.payload.data : state.items.concat(action.payload.data)
    Object.assign(state.metadata, action.payload.metadata)
  },
  [updatedTripColors.type]: (state) => {
    setReceived(state)
  },
  [failed.type]: (state) => {
    setFailed(state)
  },
  [cleaned.type]: () => initialState,
  [requestedTrip.type]: (state) => {
    setRequested(state.trip)
  },
  [receivedTrip.type]: (state, action) => {
    setReceived(state.trip)
    Object.assign(state.trip, action?.payload?.data || action.payload)
    if (action.payload.metadata) Object.assign(state.metadata, action.payload.metadata)
  },
  [createdTrip.type]: (state, action) => {
    setReceived(state.trip)
    const trip = action.payload.data || action.payload
    state.items = [trip, ...state.items]
    Object.assign(state.trip, trip)
  },
  [requestedUpdateTrip.type]: (state) => {
    setRequested(state.trip)
  },
  [updatedTrip.type]: (state, action) => {
    setReceived(state.trip)
    const trip = find(state.items, ["id", action.payload.data.id || action.payload.id]) || {}
    Object.assign(trip, action.payload.data || action.payload)
    Object.assign(state.trip, action.payload.data || action.payload)
    state.statusUpdated = !state.statusUpdated
  },
  [deletedTrip.type]: (state, action) => {
    setReceived(state.trip)
    const deletedId = action.props?.id ?? action.payload?.id
    state.items = state.items.filter((trip) => trip.id !== deletedId)
  },
  [failedTrip.type]: (state) => {
    setFailed(state.trip)
  },
  [requestedBulkTrips.type]: (state) => {
    setRequested(state.trip)
  },
  [succeededBulkTrips.type]: (state) => {
    setReceived(state.trip)
  },
  [failedBulkTrips.type]: (state) => {
    setFailed(state.trip)
  },
  [cleanedTrip.type]: (state) => {
    state.trip = initialState.trip
  },
  [requestedFishSpecies.type]: (state) => {
    setRequested(state.fish_species)
  },
  [receivedFishSpecies.type]: (state, action) => {
    setReceived(state.fish_species)
    state.fish_species.items = action.payload.data
  },
  [failedFishSpecies.type]: (state) => {
    setFailed(state.fish_species)
  },
  [requestedSuggestCategory.type]: (state) => {
    setRequested(state.fish_species)
  },
  [receivedSuggestCategory.type]: (state, action) => {
    setReceived(state.fish_species)
    state.fish_species.items = action.payload.data
  },
  [requestedSuggestMarketplaceTag.type]: () => { },
  [receivedSuggestMarketplaceTag.type]: () => { },
  [failedSuggestMarketplaceTag.type]: () => { },
  [failedSuggestCategory.type]: (state) => {
    setFailed(state.fish_species)
  },
  [requestedFilterOptions.type]: (state) => {
    setRequested(state.filter_options)
  },
  [receivedFilterOptions.type]: (state, action) => {
    setReceived(state.filter_options)
    state.filter_options.lists = action.payload
  },
  [failedFilterOptions.type]: (state) => {
    setFailed(state.filter_options)
  },
  [createdFavouriteTrips.type]: (state, action) => {
    const trip = find(state.items, ["id", action.payload.favoritable_id])
    if (trip) trip.favorite_id = action.payload.id
    if (state.trip.id) state.trip.favorite_id = action.payload.id
  },
  [deletedFavouriteTrips.type]: (state, action) => {
    const trip = find(state.items, ["favorite_id", action.payload.id])
    if (trip) trip.favorite_id = null
    if (state.trip.id) state.trip.favorite_id = null
  },
  [updatedAvailableDays.type]: (state, action) => {
    state.trip.available_days = action.payload
  },
  [updatedAvailableHours.type]: (state, action) => {
    state.trip.available_hours = action.payload
  },
  [updatedSeasonal.type]: (state, action) => {
    state.trip.seasonal = action.payload
  },
  [searchRequested.type]: (state) => {
    state.searchLoading = true
    state.searchError = null
  },
  [searchReceived.type]: (state, action) => {
    state.searchLoading = false
    state.searchItems = action.payload.data
    // Update metadata with search results metadata
    if (action.payload.metadata) {
      Object.assign(state.metadata, action.payload.metadata)
    }
  },
  [searchFailed.type]: (state, action) => {
    state.searchLoading = false
    state.searchError = action.error || action.payload
  },
  [getCategoriesRequest.type]: (state) => {
    state.categories.loading = true
  },
  [getCategoriesReceived.type]: (state, action) => {
    state.categories.loading = false
    state.categories.data = action.payload.data
  },
  [getCategoriesFailed.type]: (state, action) => {
    state.categories.loading = false
    state.categories.data = []
    state.categories.error = action.payload
  },
  [createCategoryRequest.type]: (state) => {
    state.categories.loading = true
  },
  [createCategoryReceived.type]: (state, action) => {
    state.categories.loading = false
    const newCategory = action.payload.data?.category
    if (newCategory) {
      if (!state.categories.data.categories) state.categories.data.categories = []
      state.categories.data.categories = [...state.categories.data.categories, newCategory]
    }
  },
  [createCategoryFailed.type]: (state, action) => {
    state.categories.loading = false
    state.categories.error = action.payload
  },
  [updateCategoryRequest.type]: (state) => {
    state.categories.loading = true
  },
  [updateCategoryReceived.type]: (state, action) => {
    state.categories.loading = false
    const updated = action.payload.data?.category
    if (updated && state.categories.data?.categories) {
      state.categories.data.categories = state.categories.data.categories.map((category) =>
        category.id === updated.id ? updated : category
      )
    }
  },
  [updateCategoryFailed.type]: (state, action) => {
    state.categories.loading = false
    state.categories.error = action.payload
  },
  [deleteCategoryRequest.type]: (state) => {
    state.categories.loading = true
  },
  [deleteCategoryReceived.type]: (state, action) => {
    state.categories.loading = false
    const deletedId = action.props?.id
    if (deletedId != null && state.categories.data?.categories) {
      state.categories.data.categories = state.categories.data.categories.filter((category) => category.id !== deletedId)
    }
  },
  [deleteCategoryFailed.type]: (state, action) => {
    state.categories.loading = false
    state.categories.error = action.payload
  },
  [getAddOnCategoriesRequest.type]: (state) => {
    state.addOnCategories.loading = true
  },
  [getAddOnCategoriesReceived.type]: (state, action) => {
    state.addOnCategories.loading = false
    state.addOnCategories.data = action.payload.data?.add_on_categories || []
  },
  [getAddOnCategoriesFailed.type]: (state, action) => {
    state.addOnCategories.loading = false
    state.addOnCategories.error = action.payload
  },
  [createAddOnCategoryRequest.type]: (state) => {
    state.addOnCategories.loading = true
  },
  [createAddOnCategoryReceived.type]: (state, action) => {
    state.addOnCategories.loading = false
    const newCategory = action.payload.data?.add_on_category
    if (newCategory) state.addOnCategories.data = [...state.addOnCategories.data, newCategory]
  },
  [createAddOnCategoryFailed.type]: (state, action) => {
    state.addOnCategories.loading = false
    state.addOnCategories.error = action.payload
  },
  [getMarketplaceTagsRequest.type]: (state) => {
    state.marketplaceTags.loading = true
  },
  [getMarketplaceTagsReceived.type]: (state, action) => {
    state.marketplaceTags.loading = false
    state.marketplaceTags.data = action.payload.data?.marketplace_tags || []
  },
  [getMarketplaceTagsFailed.type]: (state, action) => {
    state.marketplaceTags.loading = false
    state.marketplaceTags.data = []
    state.marketplaceTags.error = action.payload
  },
  [getExperienceOptionsRequest.type]: (state) => {
    state.experienceOptions.loading = true
  },
  [getExperienceOptionsReceived.type]: (state, action) => {
    state.experienceOptions.loading = false
    state.experienceOptions.data = action.payload.data
  },
  [getExperienceOptionsFailed.type]: (state, action) => {
    state.experienceOptions.loading = false
    state.experienceOptions.error = action.payload
  },
  // Appends the newly created item to the correct field list so the UI reflects it immediately.
  // `field` comes from action.props (passed by the thunk) since the response doesn't include it.
  // `item` is the new option from response: { data: { item: { id, name } } }
  [addExperienceOptionReceived.type]: (state, action) => {
    const field = action.props?.field
    const item = action.payload?.data?.item
    if (field && item && state.experienceOptions.data?.[field]) {
      state.experienceOptions.data[field].push(item)
    }
  },
  [addExperienceOptionRequest.type]: () => { },
  [addExperienceOptionFailed.type]: () => { },
  [getTripCategoriesRequest.type]: (state) => {
    state.tripCategories.loading = true
  },
  [getTripCategoriesReceived.type]: (state, action) => {
    state.tripCategories.loading = false
    state.tripCategories.data = action.payload.data?.categories || []
  },
  [getTripCategoriesFailed.type]: (state, action) => {
    state.tripCategories.loading = false
    state.tripCategories.data = []
    state.tripCategories.error = action.payload
  },
  [getTripSubcategoriesRequest.type]: (state) => {
    state.tripSubcategories.loading = true
  },
  [getTripSubcategoriesReceived.type]: (state, action) => {
    state.tripSubcategories.loading = false
    const raw = action.payload.data
    state.tripSubcategories.data = Array.isArray(raw) ? raw : raw?.subcategories || []
  },
  [getTripSubcategoriesFailed.type]: (state, action) => {
    state.tripSubcategories.loading = false
    state.tripSubcategories.data = []
    state.tripSubcategories.error = action.payload
  },
  [seedTripSubcategories.type]: (state, action) => {
    state.tripSubcategories.data = Array.isArray(action.payload) ? action.payload : []
  },
  [getPublicSubcategoriesRequest.type]: (state, action) => {
    const categoryId = action.props?.categoryId
    if (categoryId == null) return
    state.publicSubcategories.byCategory[categoryId] = {
      ...(state.publicSubcategories.byCategory[categoryId] || { data: [] }),
      loading: true,
      error: null
    }
  },
  [getPublicSubcategoriesReceived.type]: (state, action) => {
    const categoryId = action.props?.categoryId
    if (categoryId == null) return
    // Public endpoint returns a bare array; guard for a wrapped shape too
    const payload = action.payload
    const data = Array.isArray(payload) ? payload : payload?.subcategories || payload?.data?.subcategories || payload?.data || []
    state.publicSubcategories.byCategory[categoryId] = {
      loading: false,
      error: null,
      data: Array.isArray(data) ? data : []
    }
  },
  [getPublicSubcategoriesFailed.type]: (state, action) => {
    const categoryId = action.props?.categoryId
    if (categoryId == null) return
    state.publicSubcategories.byCategory[categoryId] = {
      loading: false,
      error: action.payload,
      data: []
    }
  },
  [CLEAR_ALL_FILTERS]: (state) => {
    return {
      ...state,
      priceRange: [],
      language: [],
      skillLevel: [],
      experiences: [],
      subcategories: [],
      other: []
    }
  },
  [setPriceRange]: (state, action) => {
    state.priceRange = action.payload
  },
  [setLanguage]: (state, action) => {
    state.language = action.payload
  },
  [setSkillLevel]: (state, action) => {
    state.skillLevel = action.payload
  },
  [setExperiences]: (state, action) => {
    state.experiences = action.payload
  },
  [setSubcategories]: (state, action) => {
    state.subcategories = action.payload
  },
  [setOther]: (state, action) => {
    state.other = action.payload
  },
  [setGuests]: (state, action) => {
    state.guests = action.payload
  },
  [setDates]: (state, action) => {
    state.dates = action.payload
  },
  [setCalendarDates]: (state, action) => {
    state.calendar = action.payload
  },
  [setGuestCount]: (state, action) => {
    state.guests = action.payload
  },
  SET_SORT: (state, action) => {
    return { ...state, sort: { ...state.sort, ...action.payload } }
  },
  [setView]: (state, action) => {
    state.view = action.payload
  }
})
export default tripsReducer

const getItems = (url, nextPage, params = {}, dispatch, getState) => {
  const state = getState()
  // Central priceRange mapping
  let mappedParams = { ...params }
  if (Array.isArray(mappedParams.priceRange)) {
    const [min_price, max_price] = mappedParams.priceRange
    mappedParams = { ...mappedParams, min_price, max_price }
    delete mappedParams.priceRange
  }
  const paginationParams = toSearchParams(formatPaginationParams(state.trips.metadata, nextPage))
  const serializedParams = toSearchParams(mappedParams, null, paginationParams)
  return dispatch(
    api({
      url,
      onStart: requested.type,
      onSuccess: received.type,
      onError: failed.type,
      params: serializedParams,
      props: { reset: !nextPage }
    })
  )
}

const getItem = (url, dispatch, additionalParams = {}) => {
  return dispatch(
    api({
      url,
      onStart: requestedTrip.type,
      onSuccess: receivedTrip.type,
      onError: failedTrip.type,
      params: { include_unavailable_dates: true, ...additionalParams }
    })
  )
}

//PUBLIC ACTIONS
export const getTrips = (nextPage, params) => (dispatch, getState) => getItems("/trips", nextPage, params, dispatch, getState)
export const getGuideTrips = (nextPage, params) => (dispatch, getState) => getItems("/guide/trips", nextPage, params, dispatch, getState)
export const getClientGuideTrips = (id, nextPage, params) => (dispatch, getState) =>
  getItems(`/client/guides/${id}/trips`, nextPage, params, dispatch, getState)

export const getTrip = (id) => (dispatch, getState) => getItem(`/trips/${id}`, dispatch)
export const getGuideTrip = (id, previewDetail, filterTripPrices = false) => (dispatch, getState) => {
  const additionalParams = filterTripPrices ? { filter_trip_prices_by_type: true } : {}
  return getItem(`/guide/trips/${id}?include_boats=${previewDetail}`, dispatch, additionalParams)
}

export const createTrip = (data) => (dispatch, getState) => {
  // Without initial data, reuse an existing empty draft if available (old wizard compat)
  if (!data) {
    const state = getState()
    const { items } = state.trips
    const emptyDraftTrip = items.find(isEmptyDraft)
    if (emptyDraftTrip) {
      dispatch(receivedTrip(emptyDraftTrip))
      return new Promise((resolve) => resolve({ data: emptyDraftTrip }))
    }
  }

  return dispatch(
    api({
      url: "/guide/trips",
      method: "post",
      data,
      onStart: requestedTrip.type,
      onSuccess: createdTrip.type,
      onError: failedTrip.type
    })
  )
}

export const updateTrip = (id, data) =>
  api({
    url: `/guide/trips/${id}`,
    method: "put",
    data: data,
    onStart: requestedUpdateTrip.type,
    onSuccess: updatedTrip.type,
    onError: failedTrip.type
  })

export const deleteTrip = (id) =>
  api({
    url: `/guide/trips/${id}`,
    method: "delete",
    props: { id },
    onStart: requestedTrip.type,
    onSuccess: deletedTrip.type,
    onError: failedTrip.type
  })

export const duplicateTrip = (id) =>
  api({
    url: `/guide/trips/${id}/duplicate`,
    method: "post",
    onStart: requestedTrip.type,
    onSuccess: createdTrip.type,
    onError: failedTrip.type
  })

/**
 * Bulk trip actions.
 *
 * Endpoints/payloads below are our proposed contract for the backend dev — adjust
 * once the API is finalised. All take an array of trip ids and the caller refetches
 * the listing on success, so the bulk response shape only needs to signal success.
 */

// PUT /guide/trips/bulk_update  { trip_ids: [...], trip: { status } }
// Used for both Activate (status: "published") and Deactivate (status: "unpublished").
export const bulkUpdateTripsStatus = (tripIds, status) =>
  api({
    url: "/guide/trips/bulk_update",
    method: "put",
    data: { trip_ids: tripIds, trip: { status } },
    onStart: requestedBulkTrips.type,
    onSuccess: succeededBulkTrips.type,
    onError: failedBulkTrips.type
  })

// DELETE /guide/trips/bulk_destroy  { trip_ids: [...] }
export const bulkDeleteTrips = (tripIds) =>
  api({
    url: "/guide/trips/bulk_destroy",
    method: "delete",
    data: { trip_ids: tripIds },
    onStart: requestedBulkTrips.type,
    onSuccess: succeededBulkTrips.type,
    onError: failedBulkTrips.type
  })

// POST /guide/trips/bulk_duplicate  { trip_ids: [...] }
export const bulkDuplicateTrips = (tripIds) =>
  api({
    url: "/guide/trips/bulk_duplicate",
    method: "post",
    data: { trip_ids: tripIds },
    onStart: requestedBulkTrips.type,
    onSuccess: succeededBulkTrips.type,
    onError: failedBulkTrips.type
  })

export const suggestCategory = (data) =>
  api({
    url: "/guide/trips/suggest_category",
    method: "post",
    data,
    onStart: requestedSuggestCategory.type,
    onSuccess: receivedSuggestCategory.type,
    onError: failedSuggestCategory.type
  })

export const suggestMarketplaceTag = (data) =>
  api({
    url: "/guide/trips/suggest_marketplace_tag",
    method: "post",
    data,
    onStart: requestedSuggestMarketplaceTag.type,
    onSuccess: receivedSuggestMarketplaceTag.type,
    onError: failedSuggestMarketplaceTag.type
  })

export const getFishSpecies = () =>
  api({
    url: `/guide/trips/fish_species`,
    onStart: requestedFishSpecies.type,
    onSuccess: receivedFishSpecies.type,
    onError: failedFishSpecies.type
  })

// Fetches predefined options for included_items, bring_items, languages, skill_levels.
// Initial state is seeded with static defaults so the UI works before this endpoint exists.
export const getExperienceOptions = () =>
  api({
    url: "/guide/trips/experience_options",
    onStart: getExperienceOptionsRequest.type,
    onSuccess: getExperienceOptionsReceived.type,
    onError: getExperienceOptionsFailed.type
  })

export const getPublicExperienceOptions = (uuid) =>
  api({
    url: `/trips/${uuid}/experience_options`,
    onStart: getExperienceOptionsRequest.type,
    onSuccess: getExperienceOptionsReceived.type,
    onError: getExperienceOptionsFailed.type
  })

// Persists a guide-created custom option so it can be reused across their experiences.
// `field` must be one of: included_items | bring_items | languages | skill_levels
// Response: { data: { item: { id, name } } } — no field or value returned.
// `field` is passed via props so the reducer can append to the correct list without relying on the response.
export const addExperienceOption = (field, name) =>
  api({
    url: "/guide/trips/experience_options",
    method: "post",
    data: { "item_type": field, name },
    props: { field },
    onStart: addExperienceOptionRequest.type,
    onSuccess: addExperienceOptionReceived.type,
    onError: addExperienceOptionFailed.type
  })

export const getFilterOptions = () =>
  api({
    url: `/trips/filter_options`,
    onStart: requestedFilterOptions.type,
    onSuccess: receivedFilterOptions.type,
    onError: failedFilterOptions.type
  })

export const getCategories = () =>
  api({
    url: `/guide/categories`,
    onStart: getCategoriesRequest.type,
    onSuccess: getCategoriesReceived.type,
    onError: getCategoriesFailed.type
  })

export const getPublicCategories = () =>
  api({
    url: `/trips/categories`,
    onStart: getCategoriesRequest.type,
    onSuccess: getCategoriesReceived.type,
    onError: getCategoriesFailed.type
  })

// Public (guest-facing) subcategories for a given category. categoryId is forwarded
// via `props` so the reducer can cache results per category.
export const getPublicSubcategories = (categoryId) =>
  api({
    url: "/trips/subcategories",
    params: { category_id: categoryId },
    props: { categoryId },
    onStart: getPublicSubcategoriesRequest.type,
    onSuccess: getPublicSubcategoriesReceived.type,
    onError: getPublicSubcategoriesFailed.type
  })

export const createCategory = (name) =>
  api({
    method: "post",
    url: `/guide/categories`,
    data: { category: { name } },
    onStart: createCategoryRequest.type,
    onSuccess: createCategoryReceived.type,
    onError: createCategoryFailed.type
  })

export const updateCategory = (id, name) =>
  api({
    method: "patch",
    url: `/guide/categories/${id}`,
    data: { category: { name } },
    onStart: updateCategoryRequest.type,
    onSuccess: updateCategoryReceived.type,
    onError: updateCategoryFailed.type
  })

export const deleteCategory = (id) =>
  api({
    method: "delete",
    url: `/guide/categories/${id}`,
    props: { id },
    onStart: deleteCategoryRequest.type,
    onSuccess: deleteCategoryReceived.type,
    onError: deleteCategoryFailed.type
  })

export const getAddOnCategories = () =>
  api({
    url: `/guide/add_on_categories`,
    onStart: getAddOnCategoriesRequest.type,
    onSuccess: getAddOnCategoriesReceived.type,
    onError: getAddOnCategoriesFailed.type
  })

export const createAddOnCategory = (name) =>
  api({
    method: "post",
    url: `/guide/add_on_categories`,
    data: { add_on_category: { name, position: 0 } },
    onStart: createAddOnCategoryRequest.type,
    onSuccess: createAddOnCategoryReceived.type,
    onError: createAddOnCategoryFailed.type
  })

export const getMarketplaceTags = () =>
  api({
    url: `/guide/trips/marketplace_tags`,
    onStart: getMarketplaceTagsRequest.type,
    onSuccess: getMarketplaceTagsReceived.type,
    onError: getMarketplaceTagsFailed.type
  })

export const getTripCategories = () =>
  api({
    url: "/guide/trips/categories",
    onStart: getTripCategoriesRequest.type,
    onSuccess: getTripCategoriesReceived.type,
    onError: getTripCategoriesFailed.type
  })

export const getTripSubcategories = (categoryId) =>
  api({
    url: "/guide/trips/subcategories",
    params: { category_id: categoryId },
    onStart: getTripSubcategoriesRequest.type,
    onSuccess: getTripSubcategoriesReceived.type,
    onError: getTripSubcategoriesFailed.type
  })
export { cleanedTrip as cleanTrip, cleaned as cleanTrips }

// SELECTORS

export const tripsWithoutEmptySelector = createSelector(collectionSelector("trips"), (trips) => reject(trips, isEmptyDraft))

export const tripLatLngSelector = createSelector(
  modelSelector("trip"),
  (trip) => trip && trip.latitude && trip.longitude && { lat: trip.latitude, lng: trip.longitude }
)

export const tripFilterOptionsSelector = (listName) =>
  createSelector(
    (state) => state.trips.filter_options.lists || {},
    (lists) => lists[listName] || []
  )

export const getGuideTripsSearch = (nextPage, params) => (dispatch, getState) => {
  const state = getState()
  const paginationParams = toSearchParams(formatPaginationParams(state.trips.metadata, nextPage))
  const serializedParams = toSearchParams(params, null, paginationParams)
  return dispatch(
    api({
      url: "/guide/trips",
      onStart: searchRequested.type,
      onSuccess: searchReceived.type,
      onError: searchFailed.type,
      params: serializedParams,
      props: { reset: !nextPage }
    })
  )
}
export const getTripList = (params = {}) => {
  return api({
    url: '/guide/trips/trip_list',
    method: 'get',
    params,
    onStart: requested.type,
    onSuccess: received.type,
    onError: failed.type
  })
}

export const updateTripColors = (tripColors) =>
  api({
    url: "/guide/trips/update_colors",
    method: "put",
    data: { trip_colors: tripColors },
    onStart: requested.type,
    onSuccess: updatedTripColors.type,
    onError: failed.type
  })
